import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import { toast } from "sonner";
import AppleSignInButton from "../components/auth/AppleSignInButton";

/**
 * Apple Light Premium Sign-In screen.
 *
 * Reviewer journey here:
 *   • Reviewer lands on a clean white iOS-style screen
 *   • Big logo + welcome
 *   • "Continue with Apple" — Apple's official renderButton
 *   • "Continue with Google" — secondary button
 *   • "Try a demo" — tertiary link so they can skip all auth
 *
 * No phone, no OTP, name is optional (and hidden behind a small "More options"
 * disclosure so the reviewer sees a 3-button minimal screen at first glance).
 */
export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const { t, lang } = useI18n();
  const ar = lang === "ar";

  const [name, setName] = useState(
    () => window.localStorage.getItem("thqah_pref_name") || "",
  );
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showEmailLogin, setShowEmailLogin] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  // Persist the (optional) name so the social-login redirect can pick it up.
  useEffect(() => {
    window.localStorage.setItem("thqah_pref_name", name);
    window.localStorage.setItem(
      "thqah_signup_intent",
      JSON.stringify({
        type: "owner",
        specialty: "camel",
        full_name: name.trim() || null,
      }),
    );
  }, [name]);

  const continueWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(ar ? `أهلاً ${u.full_name || ""}` : `Welcome ${u.full_name || ""}`);
      nav(u.role === "admin" ? "/admin" : u.role === "expert" ? "/expert" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || (ar ? "فشل تسجيل الدخول" : "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const demoLogin = async () => {
    setLoading(true);
    try {
      const u = await login("apple.review@thaqh.com", "AppleReview2026");
      toast.success(ar ? "تم الدخول كمراجع" : "Signed in as reviewer");
      nav(u.role === "admin" ? "/admin" : u.role === "expert" ? "/expert" : "/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || (ar ? "فشل وضع التجربة" : "Demo sign-in failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="ios-light min-h-screen flex flex-col"
      dir={ar ? "rtl" : "ltr"}
      data-testid="login-page"
      style={{ paddingTop: "max(2.5rem, env(safe-area-inset-top))" }}
    >
      <div className="ios-container w-full px-6 flex-1 flex flex-col">
        {/* Logo + Welcome */}
        <div className="flex flex-col items-center pt-8 pb-12">
          <img
            src="/logo192.png"
            alt="Thqah"
            className="h-20 w-20 rounded-2xl object-cover mb-6"
            style={{ boxShadow: "0 8px 24px -8px rgba(0,0,0,0.18)" }}
            data-testid="login-logo"
          />
          <h1 className="ios-large-title mb-2 text-center">
            {ar ? "أهلاً بك في ثقة" : "Welcome to Thqah"}
          </h1>
          <p className="ios-body ios-light--muted text-center max-w-xs">
            {ar ? "تقييم جمال الإبل والمواشي بالذكاء الاصطناعي" : "AI beauty evaluation for camels and livestock"}
          </p>
        </div>

        {/* Primary actions — Apple-first */}
        <div className="space-y-3">
          <AppleSignInButton />

          <button
            type="button"
            onClick={continueWithGoogle}
            disabled={loading}
            className="ios-btn-secondary"
            data-testid="login-google"
          >
            <svg className="h-[18px] w-[18px]" viewBox="0 0 48 48" aria-hidden="true">
              <path fill="#FFC107" d="M43.6 20.5h-1.9V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.4 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3c-2 1.4-4.6 2.3-7.3 2.3-5.1 0-9.5-3.3-11.2-7.9l-6.6 5.1C9.5 39.5 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.5h-1.9V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.5l6.3 5.3c-.4.4 6.4-4.7 6.4-14.3 0-1.3-.1-2.4-.4-3.5z"/>
            </svg>
            <span>{ar ? "متابعة عبر جوجل" : "Continue with Google"}</span>
          </button>

          <button
            type="button"
            onClick={demoLogin}
            disabled={loading}
            className="ios-btn-tertiary w-full"
            data-testid="login-demo"
          >
            {ar ? "تجربة سريعة" : "Try a Demo"}
          </button>
        </div>

        {/* Disclosure: Name + Email login (advanced, hidden by default) */}
        <div className="mt-10">
          {!showAdvanced && (
            <button
              type="button"
              onClick={() => setShowAdvanced(true)}
              className="w-full text-center text-[13px] ios-light--muted py-2"
              data-testid="login-show-advanced"
            >
              {ar ? "مزيد من الخيارات" : "More options"}
            </button>
          )}

          {showAdvanced && (
            <div className="space-y-4">
              <div>
                <label className="text-[13px] ios-light--muted block mb-1.5">
                  {ar ? "الاسم (اختياري)" : "Name (optional)"}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={ar ? "مثال: مبارك العنزي" : "e.g. Mubarak Al-Anzi"}
                  className="ios-input"
                  data-testid="login-name"
                />
              </div>

              {!showEmailLogin && (
                <button
                  type="button"
                  onClick={() => setShowEmailLogin(true)}
                  className="ios-btn-tertiary w-full"
                  data-testid="login-show-email"
                >
                  {ar ? "دخول بالبريد الإلكتروني" : "Sign in with email"}
                </button>
              )}

              {showEmailLogin && (
                <form onSubmit={submitEmail} className="space-y-3" data-testid="login-email-form">
                  <input
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={ar ? "البريد الإلكتروني" : "Email"}
                    required
                    className="ios-input"
                    data-testid="login-email"
                  />
                  <input
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={ar ? "كلمة المرور" : "Password"}
                    required
                    className="ios-input"
                    data-testid="login-password"
                  />
                  <button
                    type="submit"
                    disabled={loading}
                    className="ios-btn-primary"
                    data-testid="login-email-submit"
                  >
                    {loading ? "…" : ar ? "تسجيل الدخول" : "Sign in"}
                  </button>
                  <Link
                    to="/forgot-password"
                    className="ios-btn-tertiary w-full"
                    data-testid="login-forgot"
                  >
                    {ar ? "نسيت كلمة المرور؟" : "Forgot password?"}
                  </Link>
                </form>
              )}
            </div>
          )}
        </div>

        <div className="flex-1" />

        {/* Legal footer */}
        <p
          className="ios-caption text-center leading-relaxed pt-8"
          style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
        >
          {ar ? "بالمتابعة، فإنك توافق على " : "By continuing, you agree to our "}
          <Link to="/terms" className="text-[#007AFF]" data-testid="login-terms">
            {ar ? "الشروط" : "Terms"}
          </Link>
          {ar ? " و" : " and "}
          <Link to="/privacy" className="text-[#007AFF]" data-testid="login-privacy">
            {ar ? "سياسة الخصوصية" : "Privacy Policy"}
          </Link>
          .
        </p>
      </div>
    </div>
  );
}
